import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, X, ChevronDown, Banknote, Users, Wallet, Landmark, HandCoins, Eye, MapPin } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import RiyalIcon from './RiyalIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Geo ID -> name_en lowercase mapping (reverse of NAME_TO_GEO) */
const GEO_TO_NAME = {
  riyadh: 'riyadh region', makkah: 'makkah region', madinah: 'madinah region',
  eastern: 'eastern region', qassim: 'qassim region', asir: 'asir region',
  tabuk: 'tabuk region', hail: 'hail region', northern: 'northern borders region',
  jazan: 'jazan region', najran: 'najran region', bahah: 'al-bahah region', jawf: 'al-jawf region',
};

const NAME_TO_GEO = {
  'riyadh region': 'riyadh', 'makkah region': 'makkah', 'madinah region': 'madinah',
  'eastern region': 'eastern', 'qassim region': 'qassim', 'asir region': 'asir',
  'tabuk region': 'tabuk', 'hail region': 'hail', 'northern borders region': 'northern',
  'jazan region': 'jazan', 'najran region': 'najran', 'al-bahah region': 'bahah', 'al-jawf region': 'jawf',
};

/* ── Number formatting ── */
function fmtValue(v, lang) {
  if (!v && v !== 0) return '0';
  const n = Number(v);
  if (isNaN(n)) return '0';
  const isAr = lang === 'ar';
  if (n >= 1e9) {
    const d = n / 1e9;
    const num = d % 1 === 0 ? d.toFixed(0) : d.toFixed(2);
    return isAr ? `${num} مليار` : `${num} B`;
  }
  if (n >= 1e6) {
    const d = n / 1e6;
    const num = d % 1 === 0 ? d.toFixed(0) : d.toFixed(1);
    return isAr ? `${num} مليون` : `${num} M`;
  }
  if (n >= 1e4) {
    const d = n / 1e3;
    const num = d % 1 === 0 ? d.toFixed(0) : d.toFixed(1);
    return isAr ? `${num} ألف` : `${num} K`;
  }
  // Always use English digits (en-US) even for Arabic
  return n.toLocaleString('en-US');
}

function fmtNum(v, lang) {
  if (!v && v !== 0) return '0';
  const n = Number(v);
  if (n % 1 !== 0) return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return n.toLocaleString('en-US');
}

/* ── Animated counter ── */
function AnimNum({ target, duration = 1200, lang }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const n = typeof target === 'number' ? target : parseFloat(String(target).replace(/[^\d.-]/g, ''));
    if (isNaN(n) || n === 0) { setVal(0); return; }
    const t0 = performance.now();
    let raf;
    function tick(now) {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(n * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return <>{fmtValue(val, lang)}</>;
}

/* ── Detail row for modal ── */
function DetailRow({ count, label, unit, color, lang }) {
  return (
    <div className="sm-detail-row">
      <span className="sm-detail-label">{label}</span>
      <div className="sm-detail-values">
        <span className="sm-detail-count" style={{ color }}>{fmtNum(count, lang)}</span>
        {unit && <span className="sm-detail-unit" style={{ color }}>{unit}</span>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Stats Overlay — Summary Sidebar + Detail Modal
   ═══════════════════════════════════════════ */
const MapStatsOverlay = ({
  onZoomToRegion,
  isOpen,
  onOpen,
  onClose,
  hidden,
  statsGeoId,
  onStatsGeoIdChange,
}) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const t = (ar, en) => isArabic ? ar : en;
  const [regions, setRegions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Fetch regions
  useEffect(() => {
    if (!API_URL) return;
    fetch(`${API_URL}/api/regions`)
      .then(r => r.json())
      .then(data => setRegions(data || []))
      .catch(console.error);
  }, []);

  // Build geoId <-> mongoId lookup
  const geoToMongoId = useMemo(() => {
    const map = {};
    regions.forEach(r => {
      const geoId = NAME_TO_GEO[(r.name_en || '').toLowerCase()];
      if (geoId) map[geoId] = r.id;
    });
    return map;
  }, [regions]);

  const mongoIdToGeo = useMemo(() => {
    const map = {};
    regions.forEach(r => {
      const geoId = NAME_TO_GEO[(r.name_en || '').toLowerCase()];
      if (geoId) map[r.id] = geoId;
    });
    return map;
  }, [regions]);

  // Current mongo region ID from geo ID
  const currentMongoId = statsGeoId ? geoToMongoId[statsGeoId] || '' : '';

  // Fetch stats
  useEffect(() => {
    if (!isOpen || !API_URL) return;
    setLoading(true);
    setAnimate(false);
    const url = currentMongoId
      ? `${API_URL}/api/region-statistics/${currentMongoId}`
      : `${API_URL}/api/region-statistics/aggregate/all`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        setStats(data);
        setLoading(false);
        setTimeout(() => setAnimate(true), 100);
      })
      .catch(() => { setStats(null); setLoading(false); });
  }, [isOpen, currentMongoId]);

  // Dropdown change → convert mongo ID back to geoId
  const handleDropdownChange = useCallback((mongoId) => {
    if (!mongoId) {
      if (onStatsGeoIdChange) onStatsGeoIdChange('');
      return;
    }
    const geoId = mongoIdToGeo[mongoId] || '';
    if (onStatsGeoIdChange) onStatsGeoIdChange(geoId);
    if (geoId && onZoomToRegion) onZoomToRegion(geoId);
  }, [mongoIdToGeo, onStatsGeoIdChange, onZoomToRegion]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (showModal) setShowModal(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, showModal]);

  const loans = stats?.loans || {};
  const emp = stats?.empowerment_development || {};
  const sav = stats?.savings || {};
  const hasData = stats && (loans.totalLoans > 0 || emp.totalSecondStatics > 0 || sav.valueofsavings > 0);

  const selectedRegionObj = regions.find(r => r.id === currentMongoId);
  const scopeLabel = currentMongoId
    ? (isArabic
        ? (selectedRegionObj?.name_ar || '')
        : (selectedRegionObj?.name_en || selectedRegionObj?.name_ar || ''))
    : t('المملكة العربية السعودية', 'Kingdom of Saudi Arabia');

  const totalFinancing = loans.totalLoans || 0;
  const totalEmpowerment = emp.totalSecondStatics || 0;
  const totalSavings = sav.valueofsavings || 0;

  return (
    <>
      {/* ── Summary Sidebar ── */}
      {isOpen && (
        <>
          <div className="ss-backdrop" data-testid="stats-sidebar-backdrop" onClick={onClose} />
          <div className="ss-panel" data-testid="stats-sidebar-panel">

            {/* Header */}
            <div className="ss-header">
              <div className="ss-header-title">
                <BarChart3 size={16} className="ss-header-icon" />
                <h2 className="ss-title">{t('الإحصائيات', 'Statistics')}</h2>
              </div>
              <button className="ss-close" data-testid="stats-sidebar-close" onClick={onClose}>
                <X size={16} />
              </button>
            </div>

            {/* Filter */}
            <div className="ss-filter" data-testid="stats-filter-section">
              <div className="ss-filter-wrap">
                <MapPin size={14} className="ss-filter-icon" />
                <select
                  value={currentMongoId}
                  onChange={e => handleDropdownChange(e.target.value)}
                  className="ss-filter-select"
                  data-testid="stats-region-filter"
                >
                  <option value="">{t('جميع المناطق', 'All Regions')}</option>
                  {regions.filter(r => r.name_en !== 'Test Region').map(r => (
                    <option key={r.id} value={r.id}>{isArabic ? r.name_ar : (r.name_en || r.name_ar)}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="ss-filter-arrow" />
              </div>
            </div>

            {/* Scope Badge */}
            <div className="ss-scope" data-testid="stats-scope-badge">
              <span className="ss-scope-label">{scopeLabel}</span>
            </div>

            {/* Body */}
            <div className="ss-body">
              {loading ? (
                <div className="ss-loading">{t('جاري التحميل...', 'Loading...')}</div>
              ) : !hasData ? (
                <div className="ss-empty" data-testid="stats-empty">
                  <p>{currentMongoId
                    ? t('لا توجد إحصائيات لهذه المنطقة', 'No statistics for this region')
                    : t('لا توجد إحصائيات بعد', 'No statistics yet')
                  }</p>
                </div>
              ) : (
                <div className="ss-cards">
                  {/* Card 1: Financing */}
                  <div className={`ss-card ${animate ? 'ss-card-in' : ''}`} data-testid="stats-card-finance" style={{ '--card-delay': '0s' }}>
                    <div className="ss-card-header">
                      <div className="ss-card-icon" style={{ background: 'linear-gradient(135deg, #D6A84F, #B8862D)' }}>
                        <Banknote size={18} color="#fff" />
                      </div>
                      <span className="ss-card-title">{t('التمويل', 'Financing')}</span>
                    </div>
                    <div className="ss-card-metrics">
                      <div className="ss-metric" data-testid="stats-metric-financing">
                        <div className="ss-metric-icon-wrap" style={{ '--metric-color': '#D6A84F' }}>
                          <Landmark size={16} />
                        </div>
                        <div className="ss-metric-content">
                          <span className="ss-metric-value" style={{ color: '#D6A84F' }}>
                            {animate ? <AnimNum target={totalFinancing} lang={language} /> : '0'}
                            <RiyalIcon size={12} color="#D6A84F" style={{ marginRight: 4 }} />
                          </span>
                          <span className="ss-metric-label">{t('إجمالي التمويل', 'Total Financing')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Empowerment & Development */}
                  <div className={`ss-card ${animate ? 'ss-card-in' : ''}`} data-testid="stats-card-empowerment" style={{ '--card-delay': '0.1s' }}>
                    <div className="ss-card-header">
                      <div className="ss-card-icon" style={{ background: 'linear-gradient(135deg, #5DADE2, #2E86C1)' }}>
                        <HandCoins size={18} color="#fff" />
                      </div>
                      <span className="ss-card-title">{t('التمكين والتنمية', 'Empowerment & Development')}</span>
                    </div>
                    <div className="ss-card-metrics">
                      <div className="ss-metric" data-testid="stats-metric-empowerment">
                        <div className="ss-metric-icon-wrap" style={{ '--metric-color': '#5DADE2' }}>
                          <Users size={16} />
                        </div>
                        <div className="ss-metric-content">
                          <span className="ss-metric-value" style={{ color: '#5DADE2' }}>
                            {animate ? <AnimNum target={totalEmpowerment} lang={language} /> : '0'}
                            <span style={{ fontSize: '11px', opacity: 0.5, marginRight: 4 }}>{t('مستفيد', 'beneficiary')}</span>
                          </span>
                          <span className="ss-metric-label">{t('إجمالي المستفيدين', 'Total Beneficiaries')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Savings */}
                  <div className={`ss-card ${animate ? 'ss-card-in' : ''}`} data-testid="stats-card-savings" style={{ '--card-delay': '0.2s' }}>
                    <div className="ss-card-header">
                      <div className="ss-card-icon" style={{ background: 'linear-gradient(135deg, #2FAE8F, #1F7A63)' }}>
                        <Wallet size={18} color="#fff" />
                      </div>
                      <span className="ss-card-title">{t('الادخار', 'Savings')}</span>
                    </div>

                    <div className="ss-card-metrics">
                      <div className="ss-metric" data-testid="stats-metric-savings">
                        <div className="ss-metric-icon-wrap" style={{ '--metric-color': '#2FAE8F' }}>
                          <Wallet size={16} />
                        </div>
                        <div className="ss-metric-content">
                          <span className="ss-metric-value" style={{ color: '#2FAE8F' }}>
                            {animate ? <AnimNum target={totalSavings} lang={language} /> : '0'}
                            <RiyalIcon size={12} color="#2FAE8F" style={{ marginRight: 4 }} />
                          </span>
                          <span className="ss-metric-label">{t('إجمالي المدخرات', 'Total Savings')}</span>
                        </div>
                      </div>

                      <div className="ss-metric-divider" />

                      <div className="ss-savings-sub">
                        <div className="ss-savings-item" data-testid="stats-savings-beneficiaries">
                          <span className="ss-savings-num" style={{ color: '#2FAE8F' }}>{animate ? fmtNum(sav.numberofbeneficiaries, language) : '0'}</span>
                          <span className="ss-savings-label">{t('مدّخر', 'Saver')}</span>
                        </div>
                        <div className="ss-savings-item" data-testid="stats-savings-accounts">
                          <span className="ss-savings-num" style={{ color: '#2FAE8F' }}>{animate ? fmtNum(sav.numberofaccounts, language) : '0'}</span>
                          <span className="ss-savings-label">{t('حساب', 'Account')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* View Details Button */}
                  <button
                    className={`ss-details-btn ${animate ? 'ss-card-in' : ''}`}
                    data-testid="stats-view-details-btn"
                    style={{ '--card-delay': '0.3s' }}
                    onClick={() => setShowModal(true)}
                  >
                    <Eye size={16} />
                    <span>{t('عرض التفاصيل', 'View Details')}</span>
                  </button>

                  {/* Map hint */}
                  <div className={`ss-hint ${animate ? 'ss-card-in' : ''}`} style={{ '--card-delay': '0.36s' }} data-testid="stats-map-hint">
                    <MapPin size={13} />
                    <span>{t('اضغط على أي منطقة في الخريطة لعرض إحصائياتها', 'Click any region on the map to view its statistics')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Detail Modal ── */}
      {showModal && (
        <div className="sm-backdrop" data-testid="stats-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="sm-modal" data-testid="stats-detail-modal" onClick={e => e.stopPropagation()}>

            <div className="sm-header">
              <div className="sm-header-title">
                <BarChart3 size={18} className="sm-header-icon" />
                <div>
                  <h2 className="sm-title">{t('تفاصيل الإحصائيات', 'Statistics Details')}</h2>
                  <span className="sm-subtitle">{scopeLabel}</span>
                </div>
              </div>
              <button className="sm-close" data-testid="stats-modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="sm-body">
              {!hasData ? (
                <div className="ss-empty"><p>{t('لا توجد بيانات', 'No data available')}</p></div>
              ) : (
                <div className="sm-sections sm-dashboard-grid">
                  {/* Financing */}
                  <div className="sm-section" data-testid="modal-section-financing">
                    <div className="sm-section-head">
                      <div className="sm-section-icon" style={{ background: 'linear-gradient(135deg, #D6A84F, #B8862D)' }}>
                        <Banknote size={18} color="#fff" />
                      </div>
                      <div>
                        <span className="sm-section-title">{t('التمويل', 'Financing')}</span>
                        <span className="sm-section-total" style={{ color: '#D6A84F' }}>
                          {fmtValue(loans.totalLoans, language)} <RiyalIcon size={12} color="#D6A84F" />
                        </span>
                      </div>
                    </div>
                    <div className="sm-section-subtitle">
                      {t('إجمالي القروض', 'Total Loans')}: <strong style={{ color: '#D6A84F' }}>{fmtNum(loans.totalNumberOfLoans, language)} {t('قرض', 'loan')}</strong>
                    </div>
                    <div className="sm-detail-grid">
                      <DetailRow count={loans.numberOfSocialLoans} label={t('القروض الاجتماعية', 'Social Loans')} unit={t('قرض', 'loan')} color="#D6A84F" lang={language} />
                      <DetailRow count={loans.numberOfFreeLoans} label={t('قروض العمل الحر', 'Freelance Loans')} unit={t('قرض', 'loan')} color="#D6A84F" lang={language} />
                      <DetailRow count={loans.numberOfProjectsLoans} label={t('قروض المشاريع', 'Project Loans')} unit={t('قرض', 'loan')} color="#D6A84F" lang={language} />
                      <DetailRow count={loans.numberOfFamilyLoans} label={t('قروض الأسر', 'Family Loans')} unit={t('قرض', 'loan')} color="#D6A84F" lang={language} />
                    </div>
                  </div>

                  {/* Empowerment */}
                  <div className="sm-section" data-testid="modal-section-empowerment">
                    <div className="sm-section-head">
                      <div className="sm-section-icon" style={{ background: 'linear-gradient(135deg, #6366F1, #818CF8)' }}>
                        <Users size={18} color="#fff" />
                      </div>
                      <div>
                        <span className="sm-section-title">{t('التمكين والتنمية', 'Empowerment')}</span>
                        <span className="sm-section-total" style={{ color: '#818CF8' }}>{fmtNum(emp.totalSecondStatics, language)} <span style={{ fontSize: '13px', opacity: 0.5 }}>{t('مستفيد', 'beneficiary')}</span></span>
                      </div>
                    </div>
                    <div className="sm-detail-grid">
                      <DetailRow count={emp.totalnumberofbeneficiaries} label={t('إجمالي المستفيدين', 'Total Beneficiaries')} unit={t('مستفيد', 'beneficiary')} color="#818CF8" lang={language} />
                      <DetailRow count={emp.Qualificationandcapacitybuilding} label={t('التأهيل وبناء القدرات', 'Qualification & Capacity')} unit={t('برنامج', 'program')} color="#818CF8" lang={language} />
                      <DetailRow count={emp.Productionincubatorsandcommunitykitchens} label={t('الحاضنات الإنتاجية', 'Production Incubators')} unit={t('حاضنة', 'incubator')} color="#818CF8" lang={language} />
                      <DetailRow count={emp.Temporaryandpermanentsalesoutlets} label={t('منافذ البيع', 'Sales Outlets')} unit={t('منفذ', 'outlet')} color="#818CF8" lang={language} />
                    </div>
                  </div>

                  {/* Savings */}
                  <div className="sm-section" data-testid="modal-section-savings">
                    <div className="sm-section-head">
                      <div className="sm-section-icon" style={{ background: 'linear-gradient(135deg, #2FAE8F, #34D399)' }}>
                        <Wallet size={18} color="#fff" />
                      </div>
                      <div>
                        <span className="sm-section-title">{t('الادخار', 'Savings')}</span>
                        <span className="sm-section-total" style={{ color: '#2FAE8F' }}>
                          {fmtValue(sav.valueofsavings, language)} <RiyalIcon size={12} color="#2FAE8F" />
                        </span>
                      </div>
                    </div>
                    <div className="sm-detail-grid">
                      <DetailRow count={sav.numberofbeneficiaries} label={t('العملاء المدّخرون', 'Active Savers')} unit={t('مدّخر', 'saver')} color="#2FAE8F" lang={language} />
                      <DetailRow count={sav.numberofaccounts} label={t('الحسابات الادخارية', 'Savings Accounts')} unit={t('حساب', 'account')} color="#2FAE8F" lang={language} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MapStatsOverlay;
